import type { Dispatch, SetStateAction } from 'react';
import type { CategoryRecord } from '../../../catalog/domain-api';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { listCategories } from '../../../catalog/infra-api'
import { preflightCatalogSnapshotForRead } from '../../../catalog/infra-api'
import { countSearchProducts } from '../../../catalog/infra-api';

export type LoadPosCategoriesOptions = {
  /** Pull-to-refresh / acciones explícitas: fuerza preflight aunque ya haya categorías locales. */
  forceRemoteSync?: boolean;
};

/**
 * Carga categorías locales y luego remoto. Misma lógica que el refresh del POS.
 * `shouldApply` evita setState tras desmontaje (montaje inicial).
 * En montaje, si ya hay categorías en SQLite se omite el preflight remoto (lo cubre `usePosCatalogPage` + coordinador).
 */
export async function loadPosCategories(
  setCategories: Dispatch<SetStateAction<CategoryRecord[]>>,
  shouldApply: () => boolean = () => true,
  opts?: LoadPosCategoriesOptions
): Promise<void> {
  let hadLocalCategories = false;
  try {
    const localRows = await listCategories();
    if (!shouldApply()) {
      return;
    }
    hadLocalCategories = localRows.length > 0;
    setCategories(hadLocalCategories ? localRows : []);
  } catch (error) {
    if (!isDatabaseUnavailableError(error)) {
      console.warn('[POS] No se pudieron leer categorías locales', error);
    }
  }

  const skipRemote = Boolean(!opts?.forceRemoteSync && hadLocalCategories);
  if (skipRemote) {
    return;
  }

  try {
    const localProductCount = await countSearchProducts({
      includeInactive: false,
      posExcludeMisc: true,
    }).catch(() => -1);
    const forcePreflight =
      opts?.forceRemoteSync === true ||
      localProductCount === 0;
    await preflightCatalogSnapshotForRead({ force: forcePreflight });
    const localRows = await listCategories();
    if (!shouldApply()) {
      return;
    }
    setCategories(localRows);
  } catch (error) {
    if (!isDatabaseUnavailableError(error)) {
      console.warn('[POS] No se pudieron sincronizar categorías', error);
    }
  }
}
